import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';
import { isTrialExpired } from 'utils/freeTrials';

import { MainWrapper } from 'legacyComponents/styled/lib';
import { PricingAppManager } from 'apps/home/pricing/appManager';
import { Plan } from 'apps/home/pricing/views/plan';
import Heading from 'components/Heading';

import {
  PlansWrapper,
  PlanSection,
  ParagraphStyled,
  IconStyled,
} from 'apps/home/pricing/styles';

const labels = {
  freeOption: {
    title: 'Free',
    description: `Go free and develop openly with the world's largest product
    design team.`,
    checkList: [
      'Unlimited public projects',
      'Unlimited public collaborators',
      '3GB of cloud storage',
    ],
  },
  individualOption: {
    title: '€7 / month',
    description: `Go it alone for rapid project advancement for just you and your
    project stakeholders.`,
    checkList: [
      'Unlimited public projects',
      'Unlimited public collaborators',
      'Unlimited private projects',
      'Unlimited cloud storage',
      '3 private collaborators',
    ],
    additional: 'Additional users charged at €9/month',
  },
  teamOption: {
    title: '€25 / month',
    description: `Go big time with our Team plan, and grow with us as your own team
    grows.`,
    checkList: [
      'Unlimited public projects',
      'Unlimited public collaborators',
      'Unlimited private projects',
      'Unlimited cloud storage',
      'Team management',
      '5 private collaborators',
    ],
    additional: 'Additional users charged at €9/month',
  },
};

@withNamespaces()
@inject('routerStore')
@observer
export class Plans extends React.Component {
  constructor(props) {
    super(props);
    this.appManager = new PricingAppManager(null, props);
  }

  subscribeIndividual = () => {
    const { routerStore } = clientStore;
    analytics.linkedinPixel('1169153');
    routerStore.navigate('home.signUp', { trial: true });
  };

  subscribeTeam = () => {
    const { signedInProfile, routerStore } = clientStore;
    if (signedInProfile) {
      analytics.sendEvent(
        'pricing-pg-button-clicked-free-trial-team-existing-user',
      );
      routerStore.navigate('home.newInitiative', { trial: true });
    } else {
      analytics.sendEvent(
        'pricing-pg-button-clicked-free-trial-team-new-sign-up',
      );
      routerStore.navigate('home.signUpOrganisation', { trial: true });
    }
  };

  signUpIndividual = () => {
    const { routerStore } = clientStore;
    analytics.sendEvent('pricing-pg-button-clicked-sign-up');
    routerStore.navigate('home.signUp');
  };

  contact = () => {
    window.location.href =
      'mailto:team@wikifactory.com?subject=Custom Wikifactory Plan';
  };

  // TODO Consider improving this function's implementation
  getButtonConfig = (plan, space) => {
    const GET_STARTED = 'Get Started';
    const CURRENT_PLAN = 'Currrent Plan';
    const INCLUDED = 'Included';
    const START_FREE_TRIAL = 'Start Free Trial';
    const PURCHASE_SUBSCRIPTION = 'Purchase Subscription';

    const buttonConfig = {
      text: '',
      disabled: false,
    };
    const hasProSubscription = space?.hasProSubscription;
    const trialPeriodEnd = space?.trialPeriodEnd;

    switch (plan) {
      case 'Free':
        if (!space) {
          buttonConfig.text = GET_STARTED;
        } else if (
          (!hasProSubscription && !trialPeriodEnd) ||
          (!hasProSubscription && isTrialExpired(trialPeriodEnd))
        ) {
          buttonConfig.text = CURRENT_PLAN;
          buttonConfig.disabled = true;
        } else if (hasProSubscription) {
          buttonConfig.text = INCLUDED;
          buttonConfig.disabled = true;
        }
        break;
      case 'Individual':
        buttonConfig.text = START_FREE_TRIAL;
        if (!hasProSubscription && !trialPeriodEnd) {
          buttonConfig.handler = () => {
            if (this.appManager.profile) {
              analytics.sendEvent(
                'pricing-pg-button-clicked-free-trial-personal-existing-user',
              );
              this.appManager.StartFreeTrial();
            } else {
              analytics.sendEvent(
                'pricing-pg-button-clicked-free-trial-personal-new-sign-up',
              );
              this.subscribeIndividual();
            }
          };
        } else if (hasProSubscription) {
          buttonConfig.text = CURRENT_PLAN;
          buttonConfig.disabled = true;
        } else if (!hasProSubscription && isTrialExpired(trialPeriodEnd)) {
          buttonConfig.text = PURCHASE_SUBSCRIPTION;
        }
        break;
      case 'Team':
        buttonConfig.text = START_FREE_TRIAL;
        break;
      default:
        buttonConfig.text = GET_STARTED;
    }
    return buttonConfig;
  };

  render() {
    const userSpace = this.appManager.profile?.inSpace;
    const { isLoading } = this.appManager;
    const freePlanButton = this.getButtonConfig('Free', userSpace);
    const individualPlanButton = this.getButtonConfig('Individual', userSpace);
    const teamPlanButton = this.getButtonConfig('Team', userSpace);

    const cardConstructor = obj => (
      <PlanSection>
        <Heading size='large' level='2' align='center' margin='0 0 default 0'>
          <Trans>{obj.title}</Trans>
        </Heading>
        <ParagraphStyled variant='tertiary'>
          <Trans>{obj.description}</Trans>
        </ParagraphStyled>
        <ul>
          {obj.checkList.map((value, index) => (
            <li key={index}>
              <IconStyled name='check' /> <Trans>{value}</Trans>
            </li>
          ))}
        </ul>
        {obj?.additional && (
          <ParagraphStyled variant='tertiary' alignLeft>
            <Trans>{obj.additional}</Trans>
          </ParagraphStyled>
        )}
      </PlanSection>
    );

    return (
      <MainWrapper>
        <PlansWrapper>
          <Plan
            title='Free'
            color='wfBlack'
            optionOne={cardConstructor(labels.freeOption)}
            buttonText={<Trans>{freePlanButton.text}</Trans>}
            buttonDisabled={freePlanButton.disabled}
            buttonHandler={this.signUpIndividual}
          />
          <Plan
            title='Individual'
            color='primary'
            optionOne={cardConstructor(labels.individualOption)}
            buttonText={
              <Trans>
                {isLoading ? 'Starting trial...' : individualPlanButton.text}
              </Trans>
            }
            buttonDisabled={individualPlanButton.disabled}
            buttonHandler={individualPlanButton.handler}
            hasFreeTrial
          />
          <Plan
            title='Team'
            color='wfYellow'
            optionOne={cardConstructor(labels.teamOption)}
            buttonText={<Trans>{teamPlanButton.text}</Trans>}
            buttonDisabled={teamPlanButton.disabled}
            buttonHandler={this.subscribeTeam}
            hasFreeTrial
          />
        </PlansWrapper>
      </MainWrapper>
    );
  }
}

Plans.propTypes = {
  t: PropTypes.func,
};
